import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText,
  Search,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Download,
  Trash2
} from 'lucide-react';
import { ContractAnalysisJSON } from '../../../types/contract.types';
import DetailedClauseReview from '../clauses/DetailedClauseReview';
import EnhancedExecutiveSummary from '../contracts/ExecutiveSummary/EnhancedExecutiveSummary';
import { ContractAnalysisJsonParser } from '../../../services/parsers/contractAnalysisJsonParser';
import { clauseDecisionService, ClauseDecision } from '../../../services/clauseDecisionService';

interface EnhancedContractReviewProps {
  contractId: string;
  analysisId?: string; // MongoDB ObjectId for the analysis
  jsonAnalysis?: ContractAnalysisJSON;
  analysisFile?: File;
  onClauseUpdate?: (clauseRef: string, updates: any) => void;
  onRemoveAnalysis?: () => void;
}

type FilterType = 'all' | 'deal-breaker' | 'dealbreaker' | 'negotiable' | 'acceptable';
type RiskFilter = 'all' | 'high' | 'medium' | 'low';
type ClauseStatus = 'pending' | 'agreed' | 'rejected' | 'compromise';

interface ClauseComment {
  id: string;
  text: string;
  author: string;
  timestamp: Date;
  status?: ClauseStatus;
}

interface ClauseData {
  status: ClauseStatus;
  comments: ClauseComment[];
}

const EnhancedContractReview: React.FC<EnhancedContractReviewProps> = ({
  contractId,
  analysisId,
  jsonAnalysis: initialAnalysis,
  analysisFile,
  onClauseUpdate,
  onRemoveAnalysis
}) => {
  const [analysis, setAnalysis] = useState<ContractAnalysisJSON | null>(initialAnalysis || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'clauses' | 'statistics'>('summary');
  
  // Clause status and comments state
  const [clauseData, setClauseData] = useState<Record<string, ClauseData>>({});
  const [currentUser] = useState('Current User');

  // Database clause decisions for statistics
  const [dbClauseDecisions, setDbClauseDecisions] = useState<ClauseDecision[]>([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [riskFilter, setRiskFilter] = useState<RiskFilter>('all');

  // Remove confirmation
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  // Load analysis from file if provided
  useEffect(() => {
    if (analysisFile && !analysis) {
      loadAnalysisFromFile(analysisFile);
    }
  }, [analysisFile, analysis]);

  // Initialize clause data when analysis changes
  useEffect(() => {
    if (contractId && analysis) {
      // Initialize clause data for UI state
      const initialData: Record<string, ClauseData> = {};
      analysis.clauses.forEach(clause => {
        initialData[clause.reference] = {
          status: 'pending',
          comments: []
        };
      });
      setClauseData(initialData);
    }
  }, [contractId, analysis]);

  // Load clause decisions from database for statistics
  useEffect(() => {
    const loadClauseDecisions = async () => {
      if (contractId && analysisId && analysis) {
        try {
          const response = await clauseDecisionService.getClauseDecisions(contractId.toString(), analysisId);
          const decisions = (response.data as ClauseDecision[]) || [];
          setDbClauseDecisions(decisions);
        } catch (error) {
          console.error('Error loading clause decisions:', error);
          setDbClauseDecisions([]);
        }
      }
    };

    loadClauseDecisions();
  }, [contractId, analysisId, analysis]);

  // Clause data is now managed as temporary UI state only
  // Database persistence will be handled by the AnalysisUpload component

  const loadAnalysisFromFile = async (file: File) => {
    setLoading(true);
    setError(null);
    try {
      const loadedAnalysis = await ContractAnalysisJsonParser.loadFromFile(file);
      setAnalysis(loadedAnalysis);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analysis');
      console.error('Error loading analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAnalysis = () => {
    // Clear analysis data
    setAnalysis(null);
    setClauseData({});

    // Call parent callback if provided
    if (onRemoveAnalysis) {
      onRemoveAnalysis();
    }

    setShowRemoveConfirm(false);
  };

  const handleStatusChange = (clauseRef: string, status: ClauseStatus) => {
    setClauseData(prev => ({
      ...prev,
      [clauseRef]: {
        ...prev[clauseRef],
        status
      }
    }));
    
    if (onClauseUpdate) {
      onClauseUpdate(clauseRef, { status });
    }
  };

  const handleAddComment = (clauseRef: string, commentText: string) => {
    const newComment: ClauseComment = {
      id: `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: commentText,
      author: currentUser,
      timestamp: new Date(),
      status: clauseData[clauseRef]?.status
    };

    setClauseData(prev => ({
      ...prev,
      [clauseRef]: {
        ...prev[clauseRef],
        comments: [...(prev[clauseRef]?.comments || []), newComment]
      }
    }));

    if (onClauseUpdate) {
      onClauseUpdate(clauseRef, { comments: [...(clauseData[clauseRef]?.comments || []), newComment] });
    }
  };

  // Filter clauses based on current filters
  const filteredClauses = useMemo(() => {
    if (!analysis) return [];
    
    return analysis.clauses.filter(clause => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          clause.title.toLowerCase().includes(searchLower) ||
          clause.party_obligations.toLowerCase().includes(searchLower) ||
          clause.reference.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Classification filter
      if (filterType !== 'all' && clause.negotiation_analysis.classification !== filterType) {
        // Handle both 'dealbreaker' and 'deal-breaker' formats
        if (!(filterType === 'deal-breaker' && clause.negotiation_analysis.classification === 'dealbreaker')) {
          return false;
        }
      }

      // Risk filter
      if (riskFilter !== 'all') {
        const hasMatchingRisk = 
          clause.risk_assessment.legal_risk.toLowerCase().includes(riskFilter) ||
          clause.problematic_subclauses.some(sub => sub.risk_level.toLowerCase() === riskFilter);
        if (!hasMatchingRisk) return false;
      }

      return true;
    });
  }, [analysis, searchTerm, filterType, riskFilter]);

  // Calculate statistics
  const statistics = useMemo(() => {
    if (!analysis) return null;

    const totalClauses = analysis.clauses.length;
    const dealBreakers = analysis.clauses.filter(c => 
      c.negotiation_analysis.classification === 'deal-breaker' || 
      c.negotiation_analysis.classification === 'dealbreaker'
    ).length;
    const negotiable = analysis.clauses.filter(c => c.negotiation_analysis.classification === 'negotiable').length;
    const acceptable = analysis.clauses.filter(c => c.negotiation_analysis.classification === 'acceptable').length;
    
    const highRiskClauses = analysis.clauses.filter(c => 
      c.risk_assessment.legal_risk.toLowerCase().includes('high') ||
      c.problematic_subclauses.some(sub => sub.risk_level === 'High')
    ).length;

    const problematicSubclauses = analysis.clauses.reduce((sum, c) => sum + c.problematic_subclauses.length, 0);

    // Count clause statuses from database decisions
    const statusCounts = {
      agreed: 0,
      rejected: 0,
      compromise: 0,
      pending: 0
    };

    analysis.clauses.forEach(clause => {
      // Find the decision for this clause in the database
      const dbDecision = dbClauseDecisions.find(d => d.clauseReference === clause.reference);
      const status = dbDecision?.status || 'pending';
      statusCounts[status]++;
    });

    return {
      totalClauses,
      dealBreakers,
      negotiable,
      acceptable,
      highRiskClauses,
      problematicSubclauses,
      favorabilityScore: analysis.executive_summary.overall_favorability_score,
      statusCounts
    };
  }, [analysis, dbClauseDecisions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-accent-400 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading contract analysis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800/50 rounded-xl p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-error-600 dark:text-error-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-error-800 dark:text-error-300 mb-2">Error Loading Analysis</h3>
        <p className="text-error-600 dark:text-error-400">{error}</p>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="bg-gray-50 dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-xl p-6 text-center">
        <FileText className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">No Analysis Available</h3>
        <p className="text-gray-600 dark:text-gray-400">Please upload a contract analysis JSON file to begin.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header with Tabs */}
      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm dark:shadow-dark-lg border border-gray-200 dark:border-dark-border">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              Contract Analysis Review - Contract #{contractId}
            </h1>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const dataStr = ContractAnalysisJsonParser.stringify(analysis);
                  const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
                  const link = document.createElement('a');
                  link.setAttribute('href', dataUri);
                  link.setAttribute('download', `contract-${contractId}-analysis.json`);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="flex items-center px-4 py-2 bg-gray-100 dark:bg-dark-bg-secondary hover:bg-gray-200 dark:hover:bg-dark-surface-raised text-gray-700 dark:text-gray-300 rounded-lg transition-colors border border-gray-200 dark:border-dark-border"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Analysis
              </button>
              <button
                onClick={() => setShowRemoveConfirm(true)}
                className="flex items-center px-4 py-2 bg-error-50 dark:bg-error-900/20 hover:bg-error-100 dark:hover:bg-error-900/30 text-error-700 dark:text-error-400 rounded-lg transition-colors border border-error-200 dark:border-error-800/50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove Analysis
              </button>
            </div>
          </div>
        </div>

        <div className="flex border-b border-gray-200 dark:border-dark-border">
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'summary'
                ? 'text-primary-600 dark:text-accent-400 border-b-2 border-primary-600 dark:border-accent-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            Executive Summary
          </button>
          <button
            onClick={() => setActiveTab('clauses')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'clauses'
                ? 'text-primary-600 dark:text-accent-400 border-b-2 border-primary-600 dark:border-accent-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            Clause Analysis ({analysis.clauses.length})
          </button>
          <button
            onClick={() => setActiveTab('statistics')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'statistics'
                ? 'text-primary-600 dark:text-accent-400 border-b-2 border-primary-600 dark:border-accent-400'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'
            }`}
          >
            Statistics
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'summary' && (
        <EnhancedExecutiveSummary summary={analysis.executive_summary} />
      )}

      {activeTab === 'clauses' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm dark:shadow-dark-lg border border-gray-200 dark:border-dark-border p-4">
            <div className="flex items-center gap-4 flex-wrap">
              {/* Search */}
              <div className="flex-1 min-w-[300px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-accent-400/20 focus:border-primary-500 dark:focus:border-accent-400 placeholder-gray-400 dark:placeholder-gray-500"
                    placeholder="Search clauses..."
                  />
                </div>
              </div>

              {/* Classification Filter */}
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as FilterType)}
                className="px-4 py-2.5 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-accent-400/20 focus:border-primary-500 dark:focus:border-accent-400"
              >
                <option value="all">All Classifications</option>
                <option value="deal-breaker">Deal Breakers</option>
                <option value="negotiable">Negotiable</option>
                <option value="acceptable">Acceptable</option>
              </select>

              {/* Risk Filter */}
              <select
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value as RiskFilter)}
                className="px-4 py-2.5 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-accent-400/20 focus:border-primary-500 dark:focus:border-accent-400"
              >
                <option value="all">All Risk Levels</option>
                <option value="high">High Risk</option>
                <option value="medium">Medium Risk</option>
                <option value="low">Low Risk</option>
              </select>

              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing {filteredClauses.length} of {analysis.clauses.length} clauses
              </div>
            </div>
          </div>

          {/* Clause List */}
          <div className="space-y-4">
            {filteredClauses.map((clause) => (
              <DetailedClauseReview
                key={clause.reference}
                clause={clause}
                contractId={contractId.toString()}
                analysisId={analysisId || ''}
                status={clauseData[clause.reference]?.status || 'pending'}
                currentUser={currentUser}
                onUpdate={onClauseUpdate}
                onStatusChange={handleStatusChange}
                onAddComment={handleAddComment}
              />
            ))}
          </div>
        </div>
      )}

      {activeTab === 'statistics' && statistics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Overall Score */}
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm dark:shadow-dark-lg border border-gray-200 dark:border-dark-border p-6 col-span-full">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Overall Contract Favorability</h3>
            <div className="flex items-center justify-center">
              <div className="relative w-48 h-48">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="96"
                    cy="96"
                    r="80"
                    stroke="currentColor"
                    className="text-gray-200 dark:text-dark-border"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="96"
                    cy="96"
                    r="80"
                    stroke={statistics.favorabilityScore >= 7 ? '#10b981' : statistics.favorabilityScore >= 4 ? '#f59e0b' : '#ef4444'}
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${(statistics.favorabilityScore / 10) * 502.65} 502.65`}
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-gray-800 dark:text-white">{statistics.favorabilityScore}/10</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Favorability Score</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm dark:shadow-dark-lg border border-gray-200 dark:border-dark-border p-6">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300">Total Clauses</h4>
              <FileText className="w-8 h-8 text-gray-300 dark:text-gray-600" />
            </div>
            <p className="text-3xl font-bold text-gray-800 dark:text-white">{statistics.totalClauses}</p>
          </div>

          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm dark:shadow-dark-lg border border-gray-200 dark:border-dark-border p-6">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300">Deal Breakers</h4>
              <XCircle className="w-8 h-8 text-error-300 dark:text-error-600" />
            </div>
            <p className="text-3xl font-bold text-error-600 dark:text-error-400">{statistics.dealBreakers}</p>
          </div>

          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm dark:shadow-dark-lg border border-gray-200 dark:border-dark-border p-6">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300">Negotiable</h4>
              <AlertTriangle className="w-8 h-8 text-warning-300 dark:text-warning-600" />
            </div>
            <p className="text-3xl font-bold text-warning-600 dark:text-warning-400">{statistics.negotiable}</p>
          </div>

          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm dark:shadow-dark-lg border border-gray-200 dark:border-dark-border p-6">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300">Acceptable</h4>
              <CheckCircle className="w-8 h-8 text-success-300 dark:text-success-600" />
            </div>
            <p className="text-3xl font-bold text-success-600 dark:text-success-400">{statistics.acceptable}</p>
          </div>

          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm dark:shadow-dark-lg border border-gray-200 dark:border-dark-border p-6">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300">High Risk Clauses</h4>
              <AlertTriangle className="w-8 h-8 text-error-300 dark:text-error-600" />
            </div>
            <p className="text-3xl font-bold text-error-600 dark:text-error-400">{statistics.highRiskClauses}</p>
          </div>

          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm dark:shadow-dark-lg border border-gray-200 dark:border-dark-border p-6">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-700 dark:text-gray-300">Problematic Subclauses</h4>
              <AlertTriangle className="w-8 h-8 text-warning-300 dark:text-warning-600" />
            </div>
            <p className="text-3xl font-bold text-warning-600 dark:text-warning-400">{statistics.problematicSubclauses}</p>
          </div>

          {/* Clause Status Distribution */}
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm dark:shadow-dark-lg border border-gray-200 dark:border-dark-border p-6 col-span-full">
            <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-4">Clause Review Status</h4>
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <CheckCircle className="w-6 h-6 text-success-600 dark:text-success-400" />
                </div>
                <p className="text-2xl font-bold text-success-600 dark:text-success-400">{statistics.statusCounts.agreed}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Agreed</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <XCircle className="w-6 h-6 text-error-600 dark:text-error-400" />
                </div>
                <p className="text-2xl font-bold text-error-600 dark:text-error-400">{statistics.statusCounts.rejected}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Rejected</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <svg className="w-6 h-6 text-warning-600 dark:text-warning-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <p className="text-2xl font-bold text-warning-600 dark:text-warning-400">{statistics.statusCounts.compromise}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Compromise</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{statistics.statusCounts.pending}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Pending</p>
              </div>
            </div>
            <div className="mt-4 bg-gray-200 dark:bg-dark-bg-secondary rounded-full h-4 overflow-hidden">
              <div className="flex h-full">
                {statistics.statusCounts.agreed > 0 && (
                  <div
                    className="bg-success-600 dark:bg-success-500"
                    style={{ width: `${(statistics.statusCounts.agreed / statistics.totalClauses) * 100}%` }}
                  />
                )}
                {statistics.statusCounts.rejected > 0 && (
                  <div
                    className="bg-error-600 dark:bg-error-500"
                    style={{ width: `${(statistics.statusCounts.rejected / statistics.totalClauses) * 100}%` }}
                  />
                )}
                {statistics.statusCounts.compromise > 0 && (
                  <div
                    className="bg-warning-600 dark:bg-warning-500"
                    style={{ width: `${(statistics.statusCounts.compromise / statistics.totalClauses) * 100}%` }}
                  />
                )}
                {statistics.statusCounts.pending > 0 && (
                  <div
                    className="bg-gray-400 dark:bg-gray-600"
                    style={{ width: `${(statistics.statusCounts.pending / statistics.totalClauses) * 100}%` }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Remove Analysis */}
      {showRemoveConfirm && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-dark-surface rounded-xl shadow-xl dark:shadow-dark-lg border border-gray-200 dark:border-dark-border max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 bg-error-100 dark:bg-error-900/30 rounded-xl flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-error-600 dark:text-error-400" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Remove Analysis</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-gray-700 dark:text-gray-300 mb-6">
                Are you sure you want to remove this contract analysis? All clause reviews, comments, and status changes will be permanently deleted.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowRemoveConfirm(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRemoveAnalysis}
                  className="px-4 py-2 bg-error-600 dark:bg-error-500 text-white rounded-lg hover:bg-error-700 dark:hover:bg-error-600 transition-colors"
                >
                  Remove Analysis
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedContractReview; 